/**
 * On-device layout measurements.
 *
 * iOS Safari can only be remote-inspected from a Mac, and no desktop browser
 * reproduces safe-area insets, so the bottom-of-screen layout could not be
 * observed directly — it was guessed at twice, wrongly. This panel is the
 * replacement for guessing: the app reports what it measures on the device.
 *
 * Deliberately light and loud. It is a debugging instrument sitting inside a
 * pitch-black app, and the whole point is that its edges are unmistakable.
 *
 * Reached by tapping the date in the header five times, because a visible
 * control would be clutter and a query parameter cannot be typed into an
 * installed app.
 */
import { useEffect, useState } from "react";

/** Reads an `env(safe-area-inset-*)` value by letting the browser compute it. */
function readInsets(): Record<string, string> {
  const probe = document.createElement("div");
  probe.style.position = "fixed";
  probe.style.visibility = "hidden";
  probe.style.paddingTop = "env(safe-area-inset-top, 0px)";
  probe.style.paddingRight = "env(safe-area-inset-right, 0px)";
  probe.style.paddingBottom = "env(safe-area-inset-bottom, 0px)";
  probe.style.paddingLeft = "env(safe-area-inset-left, 0px)";
  document.body.append(probe);
  const style = getComputedStyle(probe);
  const insets = {
    "env inset-top": style.paddingTop,
    "env inset-right": style.paddingRight,
    "env inset-bottom": style.paddingBottom,
    "env inset-left": style.paddingLeft,
  };
  probe.remove();
  return insets;
}

/**
 * Measures what each viewport unit actually resolves to.
 *
 * The iOS bug at the heart of this is that `100%`, `100dvh` and `100svh` all
 * report the screen minus the top inset in an installed app, while `100lvh`
 * reports the whole screen. That claim came from a bug report, so the app
 * checks it on the device rather than taking it on faith.
 */
function readUnits(): Record<string, string> {
  const probe = document.createElement("div");
  probe.style.position = "fixed";
  probe.style.visibility = "hidden";
  probe.style.width = "1px";
  document.body.append(probe);
  const read = (value: string): string => {
    probe.style.height = value;
    return `${String(Math.round(probe.getBoundingClientRect().height))}px`;
  };
  const units = {
    "100vh": read("100vh"),
    "100dvh": read("100dvh"),
    "100svh": read("100svh"),
    "100lvh": read("100lvh"),
  };
  probe.remove();
  return units;
}

/** Names the element at a point, which is the one reading nothing can argue with. */
function hitTest(y: number): string {
  const element = document.elementFromPoint(Math.round(window.innerWidth / 2), y);
  if (element === null) return "nothing (outside the viewport)";
  const tag = element.tagName.toLowerCase();
  const cls = element.className;
  return typeof cls === "string" && cls.length > 0 ? `${tag}.${cls.split(" ")[0] ?? ""}` : tag;
}

/**
 * Where a `position: fixed` box actually lands.
 *
 * `getBoundingClientRect` reports layout-viewport coordinates, and the whole
 * confusion here is that those do not match the glass. A probe pinned to the
 * bottom says where the layout viewport's bottom really is.
 */
function fixedProbe(side: "top" | "bottom"): string {
  const probe = document.createElement("div");
  probe.style.position = "fixed";
  probe.style.left = "0";
  probe.style.height = "1px";
  probe.style.width = "1px";
  probe.style.visibility = "hidden";
  probe.style[side] = "0";
  document.body.append(probe);
  const box = probe.getBoundingClientRect();
  probe.remove();
  return `y ${String(Math.round(box.top))}`;
}

function rect(selector: string): string {
  const element = document.querySelector(selector);
  if (element === null) return "not found";
  const box = element.getBoundingClientRect();
  return `top ${String(Math.round(box.top))} · bottom ${String(Math.round(box.bottom))} · h ${String(Math.round(box.height))}`;
}

/**
 * What the stylesheet asks for, next to what the element got.
 *
 * A device screenshot showed a 56px tab bar after the token moved to 64px, and
 * nothing on the panel could say whether the CSS was wrong or the phone was
 * still running a cached stylesheet. These two rows separate those: a token
 * reading 56 means the old CSS is still installed, a token reading 64 beside a
 * 56px element means the rule is being overridden.
 */
function readTokens(): Record<string, string> {
  const root = getComputedStyle(document.documentElement);
  const names = ["--tabbar-h", "--tab-inset", "--tab-icon", "--tab-gap", "--tab-label"];
  return Object.fromEntries(names.map((name) => [`token ${name}`, root.getPropertyValue(name)]));
}

function collect(): Record<string, string> {
  const viewport = window.visualViewport;
  const excluded = window.screen.height - window.innerHeight;
  const bottom = window.screen.height;
  return {
    // The decisive rows first: the readout is longer than the screen.
    ".tabbar rect": rect(".tabbar"),
    ...readTokens(),
    "at y=812": hitTest(812),
    "at y=840": hitTest(840),
    "at y=870": hitTest(870),
    "fixed bottom:0": fixedProbe("bottom"),
    "fixed top:0": fixedProbe("top"),
    "body rect": rect("body"),
    "body scrollH": `${String(document.body.scrollHeight)}px`,
    "screen.height": `${String(bottom)}px`,
    "window.inner": `${String(window.innerWidth)} x ${String(window.innerHeight)}`,
    "doc.client": `${String(document.documentElement.clientWidth)} x ${String(document.documentElement.clientHeight)}`,
    visualViewport: viewport
      ? `${String(Math.round(viewport.width))} x ${String(Math.round(viewport.height))} @top ${String(Math.round(viewport.offsetTop))}`
      : "unsupported",
    screen: `${String(window.screen.width)} x ${String(window.screen.height)}`,
    "screen − window": `${String(excluded)}px excluded by the browser`,
    devicePixelRatio: String(window.devicePixelRatio),
    ...readInsets(),
    ...readUnits(),
    "standalone (media)": String(window.matchMedia("(display-mode: standalone)").matches),
    "standalone (nav)": String(
      (navigator as Navigator & { standalone?: boolean }).standalone ?? "undefined",
    ),
    html: rect("html"),
    ".app": rect(".app"),
    ".topbar": rect(".topbar"),
    ".screens": rect(".screens"),
    ".tabbar": rect(".tabbar"),
    "html scrollHeight": `${String(document.documentElement.scrollHeight)}px`,
  };
}

/**
 * Tints the real elements rather than drawing bands over them.
 *
 * The first version of this overlay painted its own coloured boxes using
 * hardcoded heights copied from the stylesheet. Those guesses did not add up to
 * the shell's real height, so it showed a black gap at the bottom that the app
 * did not have — a debugging tool reporting a bug it had invented itself.
 *
 * Tinting the actual nodes cannot do that. Anything still showing through is a
 * real gap: red is outside the shell entirely, magenta is inside the shell but
 * covered by none of its three regions.
 */
const OVERLAY_CLASS = "debug-layout";

function useLayoutOverlay(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    document.documentElement.classList.add(OVERLAY_CLASS);
    return () => {
      document.documentElement.classList.remove(OVERLAY_CLASS);
    };
  }, [active]);
}

export function Diagnostics({ onClose }: { onClose: () => void }) {
  const [rows, setRows] = useState<Record<string, string>>(collect);
  const [overlay, setOverlay] = useState(false);
  useLayoutOverlay(overlay);

  // Rotation and the URL bar showing or hiding both change every number here.
  useEffect(() => {
    const update = () => {
      setRows(collect());
    };
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
    };
  }, []);

  if (overlay) {
    return (
      <button
        type="button"
        className="overlay-exit"
        onClick={() => {
          setOverlay(false);
        }}
      >
        Back to numbers
      </button>
    );
  }

  return (
    <div className="diag">
      <div className="diag-head">
        <strong>Layout measurements</strong>
        <button type="button" onClick={onClose}>
          Close
        </button>
      </div>
      <button
        type="button"
        className="diag-overlay-toggle"
        onClick={() => {
          setOverlay(true);
        }}
      >
        Show coloured layout overlay
      </button>
      <dl className="diag-rows">
        {Object.entries(rows).map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      <p className="diag-note">Screenshot this and send it over.</p>
    </div>
  );
}
